import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    arrayMove
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MdDragIndicator } from "react-icons/md";

import {
    getMethod,
    postMethod,
    putMethod
} from "../../../../utils/api-service/api-service";

import {
    fetchCountries,
    fetchStateByCountry,
    fetchCityByState
} from "../../../../utils/api-service/get-services";

import { uploadToCloudinary, uploadToDigitalOcean } from "../../../../helpers/upload";

const CreateOffice = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const [loading, setLoading] = useState(false);

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);

    const [officeData, setOfficeData] = useState({
        label: "",
        type: "EXPERIENCIAL_STUDIO",
        upcoming: false,
        address: {
            address_line_1: "",
            address_line_2: "",
            city: "",
            state: "",
            country: "",
            map: "",
            iframe: ""
        },
        admin: {
            name: "",
            email: "",
            phone: ""
        },
        images: []
    });

    /* ============================= */
    /* INIT LOAD */
    /* ============================= */
    useEffect(() => {
        loadCountries();
        if (isEditMode) fetchOffice();
    }, [id]);

    const loadCountries = async () => {
        const res = await fetchCountries();
        if (res.success) setCountries(res.data);
    };

    /* ============================= */
    /* FETCH OFFICE (EDIT) */
    /* ============================= */
    const fetchOffice = async () => {
        const res = await getMethod(`/office/${id}`);
        if (res.success) {
            const data = res.data;

            setOfficeData({
                ...data,
                address: {
                    ...data.address,
                    country: data.address?.country?._id || "",
                    state: data.address?.state?._id || "",
                    city: data.address?.city?._id || ""
                },
                images: data.images?.map((img, i) => ({
                    id: `img-${i}`,
                    url: img.url,
                    isThumb: img.isThumb || false
                })) || []
            });

            if (data.address?.country?._id) {
                const stateRes = await fetchStateByCountry(data.address.country._id);
                if (stateRes.success) setStates(stateRes.data);
            }

            if (data.address?.state?._id) {
                const cityRes = await fetchCityByState(data.address.state._id);
                if (cityRes.success) setCities(cityRes.data);
            }
        }
    };

    /* ============================= */
    /* DROPDOWN HANDLERS */
    /* ============================= */

    const handleCountryChange = async (countryId) => {
        setOfficeData(prev => ({
            ...prev,
            address: { ...prev.address, country: countryId, state: "", city: "" }
        }));

        const res = await fetchStateByCountry(countryId);
        if (res.success) setStates(res.data);
        setCities([]);
    };

    const handleStateChange = async (stateId) => {
        setOfficeData(prev => ({
            ...prev,
            address: { ...prev.address, state: stateId, city: "" }
        }));

        const res = await fetchCityByState(stateId);
        if (res.success) setCities(res.data);
    };

    const handleCityChange = (cityId) => {
        setOfficeData(prev => ({
            ...prev,
            address: { ...prev.address, city: cityId }
        }));
    };

    /* ============================= */
    /* IMAGE HANDLING */
    /* ============================= */

    const addImages = (files) => {
        const newImages = Array.from(files).map(file => ({
            id: `img-${crypto.randomUUID()}`,
            url: URL.createObjectURL(file),
            file,
            isThumb: false
        }));

        setOfficeData(prev => ({
            ...prev,
            images: [...prev.images, ...newImages]
        }));
    };

    const removeImage = (index) => {
        setOfficeData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
    };

    const setThumbnail = (index) => {
        const updated = officeData.images.map((img, i) => ({
            ...img,
            isThumb: i === index
        }));

        setOfficeData(prev => ({ ...prev, images: updated }));
    };

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;

        const oldIndex = officeData.images.findIndex(i => i.id === active.id);
        const newIndex = officeData.images.findIndex(i => i.id === over.id);

        setOfficeData(prev => ({
            ...prev,
            images: arrayMove(prev.images, oldIndex, newIndex)
        }));
    };

    /* ============================= */
    /* VALIDATION */
    /* ============================= */

    const validateForm = () => {
        if (!officeData.address.address_line_1)
            return "Address Line 1 required";

        if (!officeData.address.country)
            return "Country required";

        if (!officeData.address.state)
            return "State required";

        if (!officeData.address.city)
            return "City required";

        if (!officeData.images.length)
            return "At least one image required";

        return null;
    };

    /* ============================= */
    /* SAVE */
    /* ============================= */

    const handleSave = async () => {
        const error = validateForm();
        if (error) return toast.error(error);

        setLoading(true);

        try {
            const uploadedImages = await Promise.all(
                officeData.images.map(async (img) => {
                    if (!img.file) return img;

                    // const res = await uploadToCloudinary(img.file, {
                    //     module: "office",
                    //     slug: officeData.type
                    // });

                    const res = await uploadToDigitalOcean(
                        img.file,
                        {
                            module: "office",
                            type: officeData.type
                        }
                    );

                    return {
                        ...img,
                        url: res.secure_url,
                        public_id: res.public_id,
                        file: undefined
                    };
                })
            );

            const payload = {
                ...officeData,
                images: uploadedImages.map(img => ({
                    url: img.url,
                    public_id: img.public_id,
                    isThumb: img.isThumb
                }))
            };

            const res = isEditMode
                ? await putMethod(`/office/${id}`, payload)
                : await postMethod(`/office`, payload);

            if (res.success) {
                toast.success("Office saved");
                navigate("/offices");
            } else {
                toast.error("Save failed");
            }

        } catch (err) {
            toast.error("Error saving office");
        } finally {
            setLoading(false);
        }
    };

    /* ============================= */
    /* UI */
    /* ============================= */

    return (
        <div className="container py-4">
            <div className="card">
                <div className="card-body">

                    {/* BASIC */}
                    <h5 className="border-bottom pb-2 mb-3">Basic Info</h5>
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Office Label</label>
                            <input
                                className="form-control"
                                placeholder="e.g. Delhi Experience Center"
                                value={officeData.label}
                                onChange={(e) =>
                                    setOfficeData(prev => ({
                                        ...prev,
                                        label: e.target.value
                                    }))
                                }
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Office Type</label>
                            <select
                                className="form-select"
                                value={officeData.type}
                                onChange={(e) =>
                                    setOfficeData(prev => ({
                                        ...prev,
                                        type: e.target.value
                                    }))
                                }
                            >
                                <option value="EXPERIENCIAL_STUDIO">Experiential Studio</option>
                                <option value="PROJECT_OFFICE">Project Office</option>
                            </select>
                        </div>

                        <div className="col-md-4">
                            <label className="form-label">Existing/Upcoming</label><br />
                            <input
                                type="checkbox"
                                checked={officeData.upcoming}
                                onChange={(e) =>
                                    setOfficeData(prev => ({
                                        ...prev,
                                        upcoming: e.target.checked
                                    }))
                                }
                            /> Upcoming
                        </div>
                    </div>

                    {/* ADDRESS */}
                    <h5 className="border-bottom mt-4 mb-3">Address Info</h5>
                    <div className="row">

                        <div className="col-md-6 mb-3">
                            <input className="form-control" placeholder="Address Line 1"
                                value={officeData.address.address_line_1}
                                onChange={(e) =>
                                    setOfficeData(prev => ({
                                        ...prev,
                                        address: { ...prev.address, address_line_1: e.target.value }
                                    }))
                                }
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <input className="form-control" placeholder="Address Line 2"
                                value={officeData.address.address_line_2}
                                onChange={(e) =>
                                    setOfficeData(prev => ({
                                        ...prev,
                                        address: { ...prev.address, address_line_2: e.target.value }
                                    }))
                                }
                            />
                        </div>

                        <div className="col-md-4 mb-3">
                            <select className="form-select"
                                value={officeData.address.country}
                                onChange={(e) => handleCountryChange(e.target.value)}
                            >
                                <option value="">Select Country</option>
                                {countries.map(c => (
                                    <option key={c._id} value={c._id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-4 mb-3">
                            <select className="form-select"
                                value={officeData.address.state}
                                onChange={(e) => handleStateChange(e.target.value)}
                            >
                                <option value="">Select State</option>
                                {states.map(s => (
                                    <option key={s._id} value={s._id}>{s.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-4 mb-3">
                            <select className="form-select"
                                value={officeData.address.city}
                                onChange={(e) => handleCityChange(e.target.value)}
                            >
                                <option value="">Select City</option>
                                {cities.map(c => (
                                    <option key={c._id} value={c._id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-12 mb-3">
                            <input className="form-control" placeholder="Google Map Link"
                                value={officeData.address.map}
                                onChange={(e) =>
                                    setOfficeData(prev => ({
                                        ...prev,
                                        address: { ...prev.address, map: e.target.value }
                                    }))
                                }
                            />
                        </div>

                        <div className="col-md-12 mb-3">
                            <input className="form-control" placeholder="Google Map I frame"
                                value={officeData.address.iframe}
                                onChange={(e) =>
                                    setOfficeData(prev => ({
                                        ...prev,
                                        address: { ...prev.address, iframe: e.target.value }
                                    }))
                                }
                            />
                        </div>
                    </div>

                    {/* ADMIN */}
                    <h5 className="border-bottom mt-4 mb-3">Admin Info</h5>
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <input className="form-control" placeholder="Name"
                                value={officeData.admin.name}
                                onChange={(e) =>
                                    setOfficeData(prev => ({
                                        ...prev,
                                        admin: { ...prev.admin, name: e.target.value }
                                    }))
                                }
                            />
                        </div>

                        <div className="col-md-4 mb-3">
                            <input className="form-control" placeholder="Email"
                                value={officeData.admin.email}
                                onChange={(e) =>
                                    setOfficeData(prev => ({
                                        ...prev,
                                        admin: { ...prev.admin, email: e.target.value }
                                    }))
                                }
                            />
                        </div>

                        <div className="col-md-4 mb-3">
                            <input className="form-control" placeholder="Phone"
                                value={officeData.admin.phone}
                                onChange={(e) =>
                                    setOfficeData(prev => ({
                                        ...prev,
                                        admin: { ...prev.admin, phone: e.target.value }
                                    }))
                                }
                            />
                        </div>
                    </div>

                    {/* IMAGES */}
                    <h5 className="border-bottom mt-4 mb-3">Images</h5>

                    <input type="file" multiple className="form-control mb-3"
                        onChange={(e) => addImages(e.target.files)}
                    />

                    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={officeData.images.map(i => i.id)} strategy={verticalListSortingStrategy}>
                            {officeData.images.map((img, index) => (
                                <SortableImageCard key={img.id} image={img} index={index} onRemove={removeImage} onSetThumb={setThumbnail} />
                            ))}
                        </SortableContext>
                    </DndContext>

                    <button className="btn btn-primary mt-4" onClick={handleSave}>
                        {loading ? "Saving..." : "Save Office"}
                    </button>

                </div>
            </div>
        </div>
    );
};

/* IMAGE CARD */
const SortableImageCard = ({ image, index, onRemove, onSetThumb }) => {
    const { setNodeRef, attributes, listeners, transform, transition } =
        useSortable({ id: image.id });

    return (
        <div ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition }}
            className="card mb-2 p-2 d-flex flex-row align-items-center"
        >
            <div {...listeners} {...attributes} style={{ cursor: "grab" }}>
                <MdDragIndicator />
            </div>

            <img src={image.url} style={{ width: 80, height: 80, marginLeft: 10 }} />

            <input type="radio" checked={image.isThumb} onChange={() => onSetThumb(index)} className="ms-3" />

            <button className="btn btn-danger btn-sm ms-auto" onClick={() => onRemove(index)}>
                Remove
            </button>
        </div>
    );
};

export default CreateOffice;